// src/components/CameraCapture.js
import React from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import "../../App.css";

export class CameraCapture extends React.Component {
    constructor(props) {
        super(props);
        this.videoRef = React.createRef();
        this.canvasRef = React.createRef();
        this.stream = null;
        this.state = {
            loading: true,
            error: null,
            devices: [],
            activeDeviceId: null,
            mirrored: false,
            visible: true, // controla exibi��o do dialog
        };
    }

    async componentDidMount() {
        await this.initCamera();
    }

    componentWillUnmount() {
        this.stopStream();
    }

    stopStream = () => {
        if (this.stream) {
            this.stream.getTracks().forEach((t) => t.stop());
            this.stream = null;
        }
    };

    pickDeviceIdForFacing = (devices, facing = "environment") => {
        const labelMatch = (label, keys) =>
            keys.some((k) => label.toLowerCase().includes(k));
        const cams = devices.filter((d) => d.kind === "videoinput");
        if (cams.length === 0) return null;

        if (facing === "environment") {
            const back = cams.find((d) =>
                labelMatch(d.label || "", ["back", "rear", "traseira"])
            );
            if (back) return back.deviceId;
        } else {
            const front = cams.find((d) =>
                labelMatch(d.label || "", ["front", "frontal"])
            );
            if (front) return front.deviceId;
        }
        return cams[0].deviceId;
    };

    startWithConstraints = async (constraints) => {
        this.stopStream();
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        this.stream = stream;
        if (this.videoRef.current) {
            this.videoRef.current.srcObject = stream;
            await this.videoRef.current.play();
        }
    };

    initCamera = async () => {
        try {
            this.setState({ loading: true, error: null });

            if (!navigator.mediaDevices?.getUserMedia) {
                throw new Error("Navegador n�o suporta acesso � webcam (getUserMedia).");
            }

            const preferredFacing = this.props.preferredFacing || "environment";
            try {
                await this.startWithConstraints({
                    video: {
                        facingMode: { ideal: preferredFacing },
                        width: { ideal: this.props.captureWidth || 1280 },
                        height: { ideal: this.props.captureHeight || 720 },
                    },
                    audio: false,
                });
            } catch {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const deviceId = this.pickDeviceIdForFacing(devices, preferredFacing);
                await this.startWithConstraints({
                    video: {
                        deviceId: deviceId ? { exact: deviceId } : undefined,
                        width: { ideal: this.props.captureWidth || 1280 },
                        height: { ideal: this.props.captureHeight || 720 },
                    },
                    audio: false,
                });
                this.setState({ devices, activeDeviceId: deviceId || null });
            }

            const mirrored = (this.props.preferredFacing || "environment") === "user";
            this.setState({ loading: false, mirrored });
        } catch (err) {
            console.error(err);
            this.setState({
                error:
                    err?.message ||
                    "N�o foi poss�vel acessar a c�mera. Verifique permiss�es do navegador.",
                loading: false,
            });
        }
    };

    switchCamera = async () => {
        try {
            const devices =
                this.state.devices.length > 0
                    ? this.state.devices
                    : await navigator.mediaDevices.enumerateDevices();

            const cams = devices.filter((d) => d.kind === "videoinput");
            if (cams.length <= 1) return;

            const currentIndex = cams.findIndex(
                (c) => c.deviceId === this.state.activeDeviceId
            );
            const nextIndex = (currentIndex + 1) % cams.length;
            const nextId = cams[nextIndex].deviceId;

            await this.startWithConstraints({
                video: {
                    deviceId: { exact: nextId },
                    width: { ideal: this.props.captureWidth || 1280 },
                    height: { ideal: this.props.captureHeight || 720 },
                },
                audio: false,
            });

            const nextLabel = cams[nextIndex].label || "";
            const mirrored = /front|frontal/i.test(nextLabel);
            this.setState({ activeDeviceId: nextId, mirrored });
        } catch (err) {
            console.error("Erro ao alternar c�mera:", err);
        }
    };

    captureAsFile = () =>
        new Promise((resolve, reject) => {
            try {
                const video = this.videoRef.current;
                const canvas = this.canvasRef.current;
                if (!video || !canvas) return reject(new Error("Sem refer�ncia de v�deo/canvas"));

                const vw = video.videoWidth || (this.props.captureWidth || 1280);
                const vh = video.videoHeight || (this.props.captureHeight || 720);
                canvas.width = vw;
                canvas.height = vh;

                const ctx = canvas.getContext("2d");
                if (this.state.mirrored) {
                    ctx.save();
                    ctx.translate(vw, 0);
                    ctx.scale(-1, 1);
                    ctx.drawImage(video, 0, 0, vw, vh);
                    ctx.restore();
                } else {
                    ctx.drawImage(video, 0, 0, vw, vh);
                }

                canvas.toBlob(
                    (blob) => {
                        if (!blob) return reject(new Error("Falha ao gerar imagem"));
                        const filename = `foto_${new Date()
                            .toISOString()
                            .replace(/[:.]/g, "-")}.png`;
                        const file = new File([blob], filename, { type: "image/png" });
                        resolve(file);
                    },
                    "image/png",
                    1
                );
            } catch (e) {
                reject(e);
            }
        });

    handleCapture = async () => {
        try {
            const file = await this.captureAsFile();
            if (typeof this.props.onCapture === "function") {
                this.props.onCapture([file]);
            }
            if (typeof this.props.onClose === "function") {
                this.props.onClose();
            }
            this.setState({ visible: false });
        } catch (e) {
            console.error("Erro ao capturar:", e);
            this.setState({ error: "N�o foi poss�vel capturar a imagem." });
        }
    };

    render() {
        const { loading, error, mirrored, visible } = this.state;

        return (
            <Dialog
                className="camera_capture_dialog"
                header="Captura de foto"
                visible={visible}
                style={{ width: "600px", maxWidth: "90vw" }}
                onHide={() => {
                    this.stopStream();
                    this.setState({ visible: false });
                    if (typeof this.props.onClose === "function") this.props.onClose();
                }}
            >
                <div className="camera_capture_dialog__content">
                    <video
                        className="camera_capture_dialog__content-video"
                        ref={this.videoRef}
                        autoPlay
                        playsInline
                        muted

                    />
                    <canvas ref={this.canvasRef} style={{ display: "none" }} />

                    {loading && <div> Ativando c&acirc;mera...</div>}
                    {error && <div style={{ color: "crimson", fontSize: 14, textAlign: "center" }}>{error}</div>}

                    <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", marginTop: 16 }}>

                        {/* Captura pelo v�deo da c�mera (getUserMedia) */}
                        <Button
                            icon="pi pi-camera"
                            className="camera_capture_dialog__content-button"
                            onClick={this.handleCapture}
                            disabled={loading || !!error}
                        >
                            Capturar foto
                        </Button>

                        {/* Captura pelo input nativo (fallback p/ celular) */}
                        {/*<Button*/}
                        {/*    icon="pi pi-replay"*/}
                        {/*    className="camera_capture_dialog__content-button"*/}
                        {/*    onClick={() => this.inputCameraRef && this.inputCameraRef.click()}>*/}
                        {/*    Mudar c&acirc;mera*/}
                        {/*</Button>*/}
                        {/*<input*/}
                        {/*    type="file"*/}
                        {/*    ref={(el) => (this.inputCameraRef = el)}*/}
                        {/*    style={{ display: "none" }}*/}
                        {/*    accept="image/*"*/}
                        {/*    capture="environment"*/}
                        {/*    onChange={(e) => {*/}
                        {/*        const files = Array.from(e.target.files);*/}
                        {/*        this.adicionarArquivos(files);*/}
                        {/*        e.target.value = "";*/}
                        {/*    }}*/}
                        {/*/>*/}

                        {/* Fechar */}
                        <Button
                            className="camera_capture_dialog__content-button"
                            icon="pi pi-times-circle"
                            onClick={() => {
                                this.stopStream();
                                this.setState({ visible: false });
                                if (typeof this.props.onClose === "function") this.props.onClose();
                            }}
                        >
                            Fechar
                        </Button>
                    </div>

                </div>
            </Dialog>
        );
    }
}
